"use client";

import { cn } from "cn";
import { CheckIcon } from "lucide-react";
import { type ReactNode, useState } from "react";

function getAnchor(text: ReactNode): string {
  if (typeof text === "string") {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/[ ]/g, "-");
  }
  if (Array.isArray(text)) {
    return text
      .map((child) => (typeof child === "string" ? child : ""))
      .join("")
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/[ ]/g, "-");
  }
  return "";
}

function AnchorLink({
  anchor,
  label,
  wasCopied,
  onClick,
}: {
  anchor: string;
  label: string;
  wasCopied: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <a
      href={`#${anchor}`}
      onClick={onClick}
      className={cn(
        "inline-flex items-center pl-1.5 min-w-9 align-baseline no-underline! text-gray-500! hover:text-gray-950! hover:opacity-100!",
        wasCopied
          ? "opacity-100"
          : "opacity-0 group-hover:opacity-100 transition-opacity group-hover:transition-none",
      )}
      aria-label={`Copy link to ${label}`}
    >
      <span className="relative inline-block w-4">
        {wasCopied ? (
          <CheckIcon className="size-4 -ml-0.25 -mb-0.5" />
        ) : (
          <span className="group-hover:opacity-100 opacity-0 transition-opacity">
            #
          </span>
        )}
      </span>
    </a>
  );
}

export function LabelWithAnchorLink({
  as: Component,
  children,
  className = "",
  anchor: customAnchor,
  skipId = false,
}: {
  as: "h2" | "h3" | "h4" | "time" | "span" | "div";
  children: ReactNode;
  className?: string;
  anchor?: string;
  skipId?: boolean;
}) {
  const anchor = customAnchor ?? getAnchor(children);
  const [wasCopied, setWasCopied] = useState(false);

  function copyAndNavigate(e: React.MouseEvent) {
    e.preventDefault();
    const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
    navigator.clipboard.writeText(url);
    setWasCopied(true);

    window.history.pushState(null, "", `#${anchor}`);
    document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });

    setTimeout(() => setWasCopied(false), 2000);
  }

  return (
    <Component
      {...(!skipId && { id: anchor })}
      className={cn("group scroll-mt-16 lg:scroll-mt-8", className)}
    >
      <span className="relative">
        <a
          href={`#${anchor}`}
          onClick={copyAndNavigate}
          className="no-underline! text-inherit! hover:text-inherit! hover:underline!"
        >
          {children}
        </a>
        <AnchorLink
          anchor={anchor}
          label={typeof children === "string" ? children : "section"}
          wasCopied={wasCopied}
          onClick={copyAndNavigate}
        />
      </span>
    </Component>
  );
}
